import React from 'react';

import { Button, Group, Modal, Stack, Text, Title } from '@mantine/core';
import KiloAlert from 'components/KiloAlert';
import { useAuth } from 'context/auth-context';
import useLinkedToDependent from 'hooks/athlete/connected/useLinkedToDependent';
import { ConnectedAthlete } from 'types';
import notificationHandler from 'utils/notifications';

interface ILinkedToDependentModalProps {
  athlete: ConnectedAthlete | null;
  opened: boolean;
  closeHandler: () => void;
}

const LinkedToDependentModal = ({
  athlete,
  opened,
  closeHandler,
}: ILinkedToDependentModalProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();
  const { session } = useAuth();
  const linkedToDependentMutation = useLinkedToDependent();

  const handleDelete = (): void => {
    linkedToDependentMutation.mutate(athlete?.athleteId || '', {
      onSuccess: () => {
        successNotification('Profile turned into dependent profile successfully');
        closeHandler();
      },
      onError: (e: any) => {
        errorNotification(`Something went wrong: ${e.response.data.message}`);
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={closeHandler}
      title={<Title order={4}>Demote to Dependent?</Title>}
      size='lg'
      trapFocus
      centered
    >
      <Stack>
        <Text color='red'>{`Warning! The selected athlete will no longer be able to log in to their profile.`}</Text>
        <Text>{`The Primary Profile will manage payments and reservations on their behalf. The selected athlete's email address will be deleted and all email notifications will be sent to the Primary Profile.`}</Text>
        {session?.gym?.hybridafEnabled && (
          <KiloAlert variantStyle='warning' data-cy='dependentHybridAfWarning'>
            Hybrid AF requires a unique email address for each member. Once this athlete shares the primary
            account holder&apos;s email, their Hybrid AF access will be removed and cannot be restored
            without converting them back to a linked athlete.
          </KiloAlert>
        )}
        <Group position='right'>
          <Button
            onClick={handleDelete}
            color='red'
            loading={linkedToDependentMutation.isLoading}
            data-cy='makeDependentContinue'
          >
            Continue
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default LinkedToDependentModal;
